/**
 * The registration a Mexican SAPI owes once it counts a foreign partner (Ley de Inversion Extranjera art. 32,
 * owner decision 2026-10-24, M-30): the SAPIs that owe it, how many business days are left, and a form to record
 * that the company filed it (the day and the folio the Registry gave). Only the designated compliance officer and
 * super admins see it; nothing of it reaches a client or an Account Executive.
 */
import { useCallback, useEffect, useState } from 'react';
import PageHeader from '../../components/layout/PageHeader';
import StatusBadge from '../../components/StatusBadge';
import { FormAlert } from '../../components/forms';
import rnieService, { type RegistrationOwed } from '../../services/rnieRegistrations.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { canRecord, owedState, partnersLine } from '../../lib/rnieRegistrations';
import { formatDate } from '../../lib/datetime';
import { colors } from '../../styles/theme';

const INPUT_STYLE = { border: `1px solid ${colors.border}` };
const INPUT_CLASS = 'w-full px-3 py-2 rounded-lg text-sm';

function RecordForm({
  item,
  onDone,
}: Readonly<{ item: RegistrationOwed; onDone: (message: string) => void }>): JSX.Element {
  const [filedOn, setFiledOn] = useState('');
  const [folio, setFolio] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const send = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await rnieService.record({ legal_entity_id: item.legal_entity_id, filed_on: filedOn, folio: folio.trim() });
      onDone('The registration is recorded.');
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not record the registration.'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-4">
      <label className="block text-xs mb-1" htmlFor={`filed-${item.legal_entity_id}`} style={{ color: colors.muted }}>
        Day the company filed it
      </label>
      <input
        id={`filed-${item.legal_entity_id}`}
        type="date"
        value={filedOn}
        onChange={(e) => setFiledOn(e.target.value)}
        className={INPUT_CLASS}
        style={INPUT_STYLE}
      />
      <label className="block text-xs mb-1 mt-3" htmlFor={`folio-${item.legal_entity_id}`} style={{ color: colors.muted }}>
        Folio the Registry gave
      </label>
      <input
        id={`folio-${item.legal_entity_id}`}
        type="text"
        value={folio}
        maxLength={100}
        onChange={(e) => setFolio(e.target.value)}
        className={INPUT_CLASS}
        style={INPUT_STYLE}
        translate="no"
      />
      <button
        type="button"
        disabled={busy || !canRecord({ filedOn, folio })}
        onClick={() => { void send(); }}
        className="mt-3 text-sm px-3 py-2 rounded-lg font-semibold"
        style={{ background: colors.gold, color: colors.navy }}
      >
        Record the registration
      </button>
      {error && <p className="text-sm mt-3" role="alert" style={{ color: colors.danger }}>{error}</p>}
    </div>
  );
}

function OwedCard({
  item,
  onDone,
}: Readonly<{ item: RegistrationOwed; onDone: (message: string) => void }>): JSX.Element {
  const state = owedState(item);
  return (
    <li className="rounded-2xl p-5" style={{ background: colors.surface, border: `1px solid ${colors.border}` }}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold" style={{ color: colors.ink }} translate="no">
            {item.entity_name ?? 'A Mexican SAPI of the group'}
          </p>
          <p className="text-xs" style={{ color: colors.muted }}>
            First counted {formatDate(item.first_counted_on)} · due {formatDate(item.due_on)}
          </p>
          <p className="text-sm mt-2" style={{ color: colors.inkSoft }}>{partnersLine(item.foreign_partners)}</p>
        </div>
        {state === 'overdue' && <StatusBadge tone="danger">Registration overdue</StatusBadge>}
        {state === 'due_today' && <StatusBadge tone="warning">Due today</StatusBadge>}
        {state === 'open' && <StatusBadge tone="warning">{item.business_days_left} business days left</StatusBadge>}
      </div>
      <RecordForm item={item} onDone={onDone} />
    </li>
  );
}

export default function RnieRegistrations(): JSX.Element {
  const [owed, setOwed] = useState<RegistrationOwed[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setOwed(await rnieService.owed());
    } catch {
      setError('Only the compliance officer and super admins can open this page.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void reload(); }, [reload]);

  return (
    <div className="max-w-4xl">
      <PageHeader
        title="Foreign investment registration"
        subtitle="A Mexican SAPI that counts a foreign partner registers with the National Registry of Foreign Investments within 40 business days."
      />
      {error && <p className="mb-4 text-sm" role="alert" style={{ color: colors.danger }}>{error}</p>}
      {notice && <div className="mb-4"><FormAlert variant="success">{notice}</FormAlert></div>}
      {loading && <p className="text-sm" style={{ color: colors.muted }}>Loading…</p>}
      {!loading && !error && owed.length === 0 && (
        <p className="text-sm" style={{ color: colors.muted }}>No registration is owed.</p>
      )}
      {!loading && !error && owed.length > 0 && (
        <ul className="space-y-3">
          {owed.map((item) => (
            <OwedCard
              key={item.legal_entity_id}
              item={item}
              onDone={(message) => { setNotice(message); void reload(); }}
            />
          ))}
        </ul>
      )}
    </div>
  );
}
